import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: "/", label: "Trang chủ" },
    { path: "/about", label: "Giới thiệu" },
    { path: "/services", label: "Dịch vụ" },
    { path: "/booking", label: "Đặt lịch" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Navbar chỉ trong suốt ở trang chủ khi chưa cuộn (hero tối).
  // Các trang khác luôn hiển thị nền nổi để không bị đè.
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-leaf-line text-lg md:text-xl" />
            </div>
            <div className="flex flex-col">
              <span
                className={`font-display text-xl md:text-2xl font-bold leading-none transition-colors ${
                  solid ? "text-primary-700" : "text-background-50"
                }`}
              >
                Xanh Wellness
              </span>
              <span
                className={`text-[10px] md:text-xs tracking-wider font-label transition-colors ${
                  solid ? "text-foreground-600" : "text-background-200"
                }`}
              >
                CHĂM SÓC TỪ TÂM
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium font-label whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? solid
                      ? "text-primary-700 bg-primary-100"
                      : "text-background-50 bg-white/20"
                    : solid
                      ? "text-foreground-700 hover:text-primary-700 hover:bg-primary-50/50"
                      : "text-background-100 hover:text-background-50 hover:bg-white/10"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {isAdmin && (
              <Link
                to="/admin/bookings"
                className={`px-4 py-2 text-sm font-medium font-label whitespace-nowrap rounded-md transition-colors flex items-center gap-1.5 ${
                  solid
                    ? "text-primary-700 bg-primary-100 hover:bg-primary-200"
                    : "text-background-50 bg-white/20 hover:bg-white/30"
                }`}
              >
                <i className="ri-dashboard-line" />
                Quản trị
              </Link>
            )}
            <Link
              to={user ? "/profile" : "/login"}
              className={`px-4 py-2 text-sm font-medium font-label whitespace-nowrap rounded-md transition-colors ${
                solid
                  ? "text-foreground-700 hover:text-primary-700"
                  : "text-background-100 hover:text-background-50"
              }`}
            >
              {user ? "Tài khoản" : "Đăng nhập"}
            </Link>
            <Link
              to="/booking"
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium font-label whitespace-nowrap rounded-md transition-colors"
            >
              Đặt lịch ngay
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              solid ? "text-foreground-800" : "text-background-50"
            }`}
            aria-label="Menu"
          >
            <i
              className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 pb-4">
          <div className="flex flex-col gap-1 pt-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-md text-sm font-medium font-label whitespace-nowrap ${
                  isActive(link.path)
                    ? "text-primary-700 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-background-200/70 mt-2 pt-2 flex flex-col gap-2">
              {isAdmin && (
                <Link
                  to="/admin/bookings"
                  className="px-4 py-3 rounded-md text-sm font-medium font-label text-primary-700 bg-primary-50 hover:bg-primary-100 whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-dashboard-line" />
                  Quản trị
                </Link>
              )}
              <Link
                to={user ? "/profile" : "/login"}
                className="px-4 py-3 rounded-md text-sm font-medium font-label text-foreground-700 hover:bg-background-100 whitespace-nowrap"
              >
                {user ? "Tài khoản" : "Đăng nhập"}
              </Link>
              <Link
                to="/booking"
                className="px-4 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-sm font-medium font-label whitespace-nowrap text-center"
              >
                Đặt lịch ngay
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}